import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Checkbox,
  CircularProgress
} from "@mui/material";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Helpers & Config;
// import { addDriver } from "./driver.service"; // Uncomment when you create the service file

const Driver = ({ onBack }) => {
  // --- STATE ---
  const [activeTab, setActiveTab] = useState("driverMaster");
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    fullName: "",
    licenseNumber: "",
    contactNumber: "",
    referenceDetails: "",
    currentSite: "",
    aadharDocUrl: "",
    panDocUrl: "",
    licenseDocUrl: ""
  });

  // Dummy Data for Driver List
  const dummyDrivers = [
    { id: 1, name: "Ramesh Kumar", license: "DL98C12345", site: "1" },
    { id: 2, name: "Suresh Patil", license: "MH04 202200", site: "3" },
    { id: 3, name: "Vinod W", license: "MH12 998877", site: "2" },
  ];

  

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSave = async () => {
    
    if (!form.fullName || !form.licenseNumber) {
      alert("Please fill in required fields");
      return;
    }

    try {
    
      console.log("Saving Driver Data:", form); 
      
      alert("Driver Saved Successfully!");
      
      // Reset form
      setForm({
        fullName: "",
        licenseNumber: "",
        contactNumber: "",
        referenceDetails: "",
        currentSite: "",
        aadharDocUrl: "",
        panDocUrl: "",
        licenseDocUrl: ""
      });

      if(onBack) onBack(); 

    } catch (error) {
      console.error("Error:", error);
      alert("Error saving data");
    }
  };

  // Mock Upload Handler
  const handleFileUpload = (e, fieldName) => {
      // Add your Firebase upload logic here (copied from Vehicle.jsx)
      alert(`Upload logic for ${fieldName} goes here`);
  };

  return (
    <Box>
        {/* HEADER SECTION */}
        <Box mb={3}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Driver Management Master
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage driver details, licenses, and compliance
            </Typography>
        </Box>

        {/* TOP TABS */}
        <Box mb={3} display="flex" gap={1}>
            <TabButton 
                active={activeTab === "driverMaster"} 
                onClick={() => setActiveTab("driverMaster")}
            >
                Driver Master Data
            </TabButton>
            <TabButton 
                active={activeTab === "serviceDB"} 
                onClick={() => {
                    setActiveTab("serviceDB");
                    // Logic to switch to Service Center view if integrated
                    // if(onSwitchToService) onSwitchToService();
                }}
            >
                Service Center Database
            </TabButton>
        </Box>

        {/* MAIN CONTENT GRID */}
        <Grid container spacing={3}>
            
            {/* === LEFT COLUMN: FORM === */}
            <Grid item xs={12} md={5}>
                <SitePage>
                    <SectionHeader>Add New Driver</SectionHeader>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Full Name"
                            name="fullName"
                            value={form.fullName}
                            onChange={handleChange}
                            placeholder="Ramesh Kumar"
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="License Number"
                            name="licenseNumber"
                            value={form.licenseNumber}
                            onChange={handleChange}
                            placeholder="DL98C12345"
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Contact Number"
                            name="contactNumber"
                            value={form.contactNumber}
                            onChange={handleChange}
                            placeholder="9876543210"
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            fullWidth
                            size="small"
                            label="Reference Details"
                            name="referenceDetails"
                            value={form.referenceDetails}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel shrink>Current Site Assigned</InputLabel>
                            <Select
                                native
                                name="currentSite"
                                value={form.currentSite}
                                onChange={handleChange}
                                label="Current Site Assigned"
                            >
                                <option value="">Select Site...</option>
                                <option value="1">Site A - Kalamboli Yard</option>
                                <option value="2">Site B - Panvel</option>
                                <option value="3">Site C - JNPT</option>
                            </Select>
                        </FormControl>

                        {/* UPLOAD DOCUMENTS SECTION */}
                        <Box mt={1}>
                            <Typography variant="caption" fontWeight="bold" color="textSecondary" gutterBottom>
                                Upload Documents
                            </Typography>
                            
                            {/* Aadhar Card Row */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={1}>
                                <Typography variant="body2">Aadhar Card</Typography>
                                <Button 
                                    size="small" 
                                    variant="contained" 
                                    component="label"
                                    startIcon={<CloudUploadIcon />}
                                                                            sx={{
                                            backgroundColor: "#D32F2F",
                                            "&:hover": { backgroundColor: "#9A0007" }
                                        }}
                                >
                                    Upload
                                    <input type="file" hidden onChange={(e) => handleFileUpload(e, 'aadharDocUrl')} />
                                </Button>
                            </Box>

                            {/* PAN Card Row */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2">PAN Card</Typography>
                                <Button 
                                size="small" 
                                variant="outlined" 
                                startIcon={<VisibilityIcon />}
                                sx={{
                                    textTransform: "none",
                                    color: "#D32F2F",
                                    borderColor: "#D32F2F",
                                    "&:hover": {
                                        borderColor: "#9A0007",
                                        backgroundColor: "rgba(211,47,47,0.08)",
                                        color: "#9A0007"
                                    }
                                }}
                            >
                                View
                            </Button>
                            </Box>

                            {/* License Row */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="body2">Driver's License</Typography>
                             <Button 
                                size="small" 
                                variant="outlined" 
                                startIcon={<VisibilityIcon />}
                                sx={{
                                    textTransform: "none",
                                    color: "#D32F2F",
                                    borderColor: "#D32F2F",
                                    "&:hover": {
                                        borderColor: "#9A0007",
                                        backgroundColor: "rgba(211,47,47,0.08)",
                                        color: "#9A0007"
                                    }
                                }}
                            >
                                View
                            </Button>
                            </Box>
                        </Box>

                        {/* BUTTONS */}
                        <Box display="flex" gap={2} mt={2}>
                            <Button 
                                variant="outlined" 
                                color="inherit" 
                                fullWidth
                                onClick={onBack}
                                startIcon={<ArrowBackIcon />}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                fullWidth
                                startIcon={<SaveIcon />}
                                onClick={handleSave}
                                sx={{
    backgroundColor: "#D32F2F",
    "&:hover": { backgroundColor: "#9A0007" }
  }}
                            >
                                Save Driver
                            </Button>
                        </Box>
                    </Box>
                </SitePage>
            </Grid>

            {/* === RIGHT COLUMN: LIST & DETAILS === */}
            <Grid item xs={12} md={7}>
                <SitePage>
                    
                    {/* Search Bar */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} p={1} bgcolor="#f5f5f5" borderRadius={1}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ ml: 1 }}>
                            Driver List
                        </Typography>
                        <TextField 
                            size="small" 
                            placeholder="Search..."
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ bgcolor: "white", px: 1, borderRadius: 1 }}
                        />
                    </Box>

                    {/* TABLE 1: Driver List */}
                    <TableContainer sx={{ mb: 4, border: "1px solid #eee", maxHeight: "300px" }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <StyledHeaderCell>Name</StyledHeaderCell>
                                    <StyledHeaderCell>License</StyledHeaderCell>
                                    <StyledHeaderCell align="right">Current Site</StyledHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dummyDrivers.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.license}</TableCell>
                                        <TableCell align="right">{row.site}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* EXPANSION / DETAIL VIEW */}
                    <SectionHeader>Expansion / Detail View</SectionHeader>
                    
                    <Box bgcolor="#f9f9f9" p={2} borderRadius={2} border="1px solid #eee">
                        <Grid container spacing={2} alignItems="center" mb={2}>
                            <Grid item xs={4}>
                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    label="Date" 
                                    defaultValue="2023-01-10" 
                                    InputLabelProps={{ shrink: true }} 
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    label="Vehicle Plate Description" 
                                    defaultValue="MH12AB4567" 
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField 
                                    fullWidth 
                                    size="small" 
                                    label="Claim Utilized?" 
                                    defaultValue="Yes" 
                                    InputLabelProps={{ shrink: true }} 
                                />
                            </Grid>
                        </Grid>

                        {/* TABLE 2: Fine History */}
                        <TableContainer sx={{ border: "1px solid #e0e0e0" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <StyledHeaderCell>Penalty/Fine History</StyledHeaderCell>
                                        <StyledHeaderCell>Type</StyledHeaderCell>
                                        <StyledHeaderCell align="right">Status</StyledHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Rs 2-05-20</TableCell>
                                        <TableCell>Speeding</TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption" color="primary" sx={{cursor:'pointer'}}>
                                                Link to Fines Log ►
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                </SitePage>
            </Grid>

        </Grid>
    </Box>
  );
};

// --- STYLED COMPONENTS ---

const SitePage = styled.div`
  padding: 25px;
  background-color: #fff;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  height: 100%;
`;

const SectionHeader = styled.h6`
  font-weight: 700;
  font-size: 1rem;
  color: #333;
  margin-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
`;

const TabButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${props => props.active ? "#D32F2F" : "#fff"};
  color: ${props => props.active ? "#fff" : "#333"};
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  transition: all 0.2s;

  &:hover {
   background-color: ${props => props.active ? "#D32F2F" : "#fff"};
  }
`;

const StyledHeaderCell = styled(TableCell)`
  && {
    background-color: #333; // Dark header to match image
    color: white;
    font-weight: bold;
    padding: 8px 16px;
  }
`;

export default Driver;